import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import React from 'react';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import OccurrenceReportDescriptorService from '../../../services/occurrence-report-descriptor.service';
import OccurrenceReportDescriptor from '../../../@types/occurrence-report-descriptor';
import {ResourceType} from '../../../@types/resource-type';
import {toast} from 'react-toastify';
import DotdotdotButton from '../../form-components/DotdotdotButton.component';
import DeleteResource from '../modals/DeleteResource.component';
import Box from '@mui/material/Box';
import {UserContext} from '../../../context/userContext';

export default function OccurrenceReportDescriptorActionsMenu(props: {
    occurrenceReportDescriptor: OccurrenceReportDescriptor
}): React.ReactElement {
    const [confirmDeleteOccurrenceReportDescriptor, setConfirmDeleteOccurrenceReportDescriptor] = React.useState<boolean>(false);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

    const { user } = React.useContext(UserContext);
    const { setCurrentWorkspaceObject,
        workspaceObjects, setWorkspaceObjects,
        setDeletedWorkspaceObject,
        getAdditionalResource, publishResourceUpdate,
        currentWorkspaceObject } = useWorkspaceContext();
    const { occurrenceReportDescriptor } = props;

    const isLegacyExplorer = localStorage.getItem('isam.explorer.useNew') === 'false';
 
    function onProperties(): void {
        setCurrentWorkspaceObject({ objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS, object: occurrenceReportDescriptor });
    }

    function onDelete(): void {
        setConfirmDeleteOccurrenceReportDescriptor(true);
    }

    /**
     * Check if the user can delete the occurrence report descriptor.
     * Published descriptors may only be deleted by superusers.
     * Draft descriptors require ANALYST or ADMIN membership in the owning group.
     */
    function canDeleteOccurrenceReportDescriptor(): boolean {
        if (occurrenceReportDescriptor.descriptorState === 'PUBLISHED') {
            return user?.superUser ?? false;
        }
        const groups = getAdditionalResource(ResourceType.GROUPS).resources;
        const group = groups?.find((grp) => grp.id === occurrenceReportDescriptor.group.id);
        return group ? group.canEdit() : true;
    }

    return (
        <Box tabIndex={0}  onClick={(event: any): void => {
            event.stopPropagation();
        }}>
            <DotdotdotButton>
                <MenuItem id='properties' onClick={onProperties}>Properties</MenuItem>
                <Divider />
                <MenuItem
                    id='delete'
                    onClick={onDelete}
                    disabled={!canDeleteOccurrenceReportDescriptor()}
                >
                    Delete
                </MenuItem>
            </DotdotdotButton>
            <DeleteResource
                open={confirmDeleteOccurrenceReportDescriptor}
                resourceType={ResourceType.OCCURRENCE_REPORT_DESCRIPTORS}
                resourceName={occurrenceReportDescriptor.name}
                subResources={'descriptor terms and resources'}
                handleDelete={(): void => {
                    if (isDeleting) {
                        return;
                    }
                    
                    setIsDeleting(true);
                    
                    // Clear currentWorkspaceObject FIRST if it's the one being deleted
                    if (currentWorkspaceObject?.objectType === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS && 
                        currentWorkspaceObject?.object?.id === occurrenceReportDescriptor.id) {
                        setCurrentWorkspaceObject(null);
                    }
                    
                    OccurrenceReportDescriptorService.deleteDescriptorById(occurrenceReportDescriptor.id!).then(() => {
                        // Notify tab editor to close the tab
                        setDeletedWorkspaceObject({
                            objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
                            id: occurrenceReportDescriptor.id!
                        });
                        
                        // Then publish the delete event to notify explorer
                        publishResourceUpdate({
                            type: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
                            id: occurrenceReportDescriptor.id!.toString(),
                            source: 'delete',
                            updatedData: null
                        });
                        if (isLegacyExplorer) {
                            // If using the legacy explorer, refresh the tree
                            if (workspaceObjects?.objectType === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS) {
                                setWorkspaceObjects({objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS});
                            }                            
                        }
                    }).catch((error) => {
                        toast.error(error.message);
                    }).finally(() => {
                        setIsDeleting(false);
                        setConfirmDeleteOccurrenceReportDescriptor(false);
                    });

                }}
                onClose={(): void => {
                    setConfirmDeleteOccurrenceReportDescriptor(false);
                }}
            />
        </Box>
    );
}
